import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Input, Area, Select, ScheduleControls } from "./fields";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";

const uid = () => `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
const TYPES = ["Tournage", "Atelier", "Événement", "Formation", "Festival"].map((t) => ({ value: t, label: t }));

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>{children}</label>
);
const rawCls = "mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none disabled:opacity-40";

export const AdminEvents = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(null);
  const items = value.items || [];
  const setField = (k, v) => onChange({ ...value, [k]: v });
  const setItems = (arr) => onChange({ ...value, items: arr });
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; setItems(c); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setItems(c); };
  const add = () => {
    setItems([...items, { id: uid(), title: "Nouvel événement", type: "Événement", date: "", endDate: "", time: "", location: "", description: "", spotsTotal: 0, spotsTaken: 0, status: "published", publishAt: "" }]);
    setOpen(items.length);
  };

  return (
    <div className="space-y-6" data-testid="admin-events-editor">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">
        Ajoutez vos <strong className="text-white">tournages, ateliers et événements</strong>. Ils apparaissent automatiquement dans le <a href="/calendrier" target="_blank" rel="noreferrer" className="text-electric">calendrier</a>, aux côtés des sessions de formation et du festival.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Sur-titre" value={value.eyebrow} onChange={(v) => setField("eyebrow", v)} />
        <Input label="Titre de section" value={value.heading} onChange={(v) => setField("heading", v)} />
      </div>
      <Area label="Introduction" value={value.intro} onChange={(v) => setField("intro", v)} />

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Événements ({items.length})</span>
        <button onClick={add} data-testid="admin-add-event" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter un événement</button>
      </div>

      {open !== null && items[open] ? (
        <FicheOverlay testid="event-fiche" title={items[open].title} subtitle={items[open].type || "Événement"} onClose={() => setOpen(null)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Titre" value={items[open].title} onChange={(v) => patch(open, { title: v })} />
            <Select label="Type" value={items[open].type} onChange={(v) => patch(open, { type: v })} options={TYPES} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Date de début"><input type="date" value={items[open].date || ""} onChange={(e) => patch(open, { date: e.target.value })} className={rawCls} data-testid={`event-date-${open}`} /></Field>
            <Field label="Date de fin (si plusieurs jours)"><input type="date" value={items[open].endDate || ""} onChange={(e) => patch(open, { endDate: e.target.value })} className={rawCls} /></Field>
            <Field label="Heure de début"><input type="time" value={items[open].time || ""} onChange={(e) => patch(open, { time: e.target.value })} className={rawCls} disabled={!!items[open].allDay} data-testid={`event-starttime-${open}`} /></Field>
            <Field label="Heure de fin"><input type="time" value={items[open].endTime || ""} onChange={(e) => patch(open, { endTime: e.target.value })} className={rawCls} disabled={!!items[open].allDay} data-testid={`event-endtime-${open}`} /></Field>
          </div>
          <label className="flex items-center gap-2 text-xs text-secondary2">
            <input type="checkbox" checked={!!items[open].allDay} onChange={(e) => patch(open, { allDay: e.target.checked })} className="accent-electric" data-testid={`event-allday-${open}`} />
            Toute la journée (masque les heures sur le site)
          </label>
          <Input label="Lieu" value={items[open].location} onChange={(v) => patch(open, { location: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Places totales (0 = illimité)" value={items[open].spotsTotal} onChange={(v) => patch(open, { spotsTotal: v })} />
            <Input label="Places prises" value={items[open].spotsTaken} onChange={(v) => patch(open, { spotsTaken: v })} />
          </div>
          <Area label="Description" value={items[open].description} onChange={(v) => patch(open, { description: v })} rows={2} />
          <ScheduleControls status={items[open].status} publishAt={items[open].publishAt} onChange={(p) => patch(open, p)} />
        </FicheOverlay>
      ) : (
        <>
          {items.length === 0 && <p className="text-secondary2 text-sm">Aucun événement.</p>}
          <CardGrid testid="events-cards">
            {items.map((it, i) => (
              <ItemCard key={it.id || i} testid={`admin-event-item-${i}`}
                title={it.title} subtitle={[it.date, it.time, it.location].filter(Boolean).join(" · ")}
                dimmed={it.status === "hidden"}
                badges={[{ label: it.type || "Événement", tone: "text-electric border-electric/40 bg-black/60" }]}
                onOpen={() => setOpen(i)}
                actions={
                  <>
                    <button onClick={() => move(i, -1)} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                    <button onClick={() => move(i, 1)} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                    <button onClick={() => setItems(items.filter((_, x) => x !== i))} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                  </>
                } />
            ))}
          </CardGrid>
        </>
      )}
    </div>
  );
};
